import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

// Sesión gestionada por Supabase Auth.
//
// Antes el acceso se resolvía consultando la tabla "users" y comparando la
// contraseña en texto plano, con la sesión guardada en localStorage. Eso
// significaba que la contraseña viajaba en una consulta, estaba almacenada sin
// cifrar y cualquiera con la clave anónima —que va incrustada en el JavaScript
// público— podía leer la tabla entera de usuarios. Ahora la contraseña la
// verifica Supabase, nunca se almacena en claro y la sesión es un token con
// caducidad y renovación automática.

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);

  // Datos de presentación (nombre, cargo) asociados a la cuenta. La tabla
  // "users" solo es legible con sesión iniciada.
  const cargarPerfil = async (usuario) => {
    if (!usuario) {
      setPerfil(null);
      return;
    }

    // Perfil de reserva: si la consulta falla —sin permisos, sin red, sin ficha
    // en la tabla— el panel debe abrirse igual. El nombre es un detalle de
    // presentación, no una condición para trabajar.
    const deReserva = {
      id: usuario.id,
      email: usuario.email,
      name: usuario.user_metadata?.name || usuario.email.split('@')[0],
      role: usuario.user_metadata?.role || 'admin',
    };

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, charge')
        .eq('email', usuario.email)
        .maybeSingle();

      if (error) console.warn('No se pudo cargar el perfil:', error.message);
      setPerfil(data || deReserva);
    } catch (e) {
      console.warn('No se pudo cargar el perfil:', e.message);
      setPerfil(deReserva);
    }
  };

  useEffect(() => {
    let activo = true;

    // El try/finally es imprescindible: si algo falla aquí y no se apaga
    // "loading", el panel se queda con el círculo girando indefinidamente y no
    // llega ni a la pantalla de acceso.
    const iniciar = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!activo) return;
        if (error) console.warn('No se pudo recuperar la sesión:', error.message);
        setSession(data?.session ?? null);
        // El perfil se pide aparte, sin await: si la red va lenta o la consulta
        // se cuelga, el panel debe abrirse igual. El nombre es presentación, no
        // una condición para entrar.
        cargarPerfil(data?.session?.user);
      } catch (e) {
        console.error('Error al iniciar la sesión:', e);
        if (activo) setSession(null);
      } finally {
        if (activo) setLoading(false);
      }
    };

    iniciar();

    // Mantiene la sesión al día: renovación del token, cierre en otra pestaña...
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, nuevaSesion) => {
      if (!activo) return;
      setSession(nuevaSesion);
      setLoading(false);
      cargarPerfil(nuevaSesion?.user);
    });

    return () => {
      activo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      // No se distingue entre "correo inexistente" y "contraseña incorrecta":
      // eso permitiría averiguar qué cuentas existen.
      throw new Error('Credenciales inválidas. Verifica tu correo y contraseña.');
    }

    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setPerfil(null);
  };

  return (
    <AuthContext.Provider value={{ user: perfil, session, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};
